import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const defaultControls = {
  universeSize: 100,
  exchange: 'BINANCE',
  timeframe: 'ALL',
  symbolFilter: '',
};

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('fr-FR');
}

function formatInteger(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0';
  }
  return value.toLocaleString('fr-FR');
}

function buildSnapshotMessage(payload, fallbackMessage) {
  const rowCount = typeof payload?.exportedRows === 'number'
    ? `${formatInteger(payload.exportedRows)} lignes exportees`
    : null;
  return [payload?.message || fallbackMessage, rowCount].filter(Boolean).join(' - ');
}

const PatternMarketDataAdminPage = () => {
  const [controls, setControls] = useState(defaultControls);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  const buildQuery = useCallback(() => {
    const query = new URLSearchParams({
      universeSize: String(Math.max(1, Number(controls.universeSize) || 100)),
      exchange: controls.exchange || 'BINANCE',
    });
    return query.toString();
  }, [controls.exchange, controls.universeSize]);

  const loadStatus = useCallback(async (withSpinner = true) => {
    if (withSpinner) {
      setLoading(true);
    }
    setError('');
    try {
      const response = await fetch(`/api/admin/job/pattern-market-data-status?${buildQuery()}`);
      if (!response.ok) {
        throw new Error('Impossible de charger le statut du bootstrap historique.');
      }
      setStatusData(await response.json());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Erreur inconnue');
    } finally {
      if (withSpinner) {
        setLoading(false);
      }
    }
  }, [buildQuery]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!statusData?.running) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      void loadStatus(false);
    }, 3000);
    return () => window.clearInterval(intervalId);
  }, [loadStatus, statusData?.running]);

  const handleAction = async (action) => {
    const actionConfig = {
      bootstrap: {
        path: 'pattern-market-data-bootstrap',
        withQuery: true,
        fallbackMessage: 'Bootstrap termine.',
      },
      sync: {
        path: 'pattern-market-data-sync',
        withQuery: true,
        fallbackMessage: 'Synchro terminee.',
      },
      'export-candles': {
        path: 'pattern-market-data-export-import-csv',
        withQuery: false,
        fallbackMessage: 'Snapshot CSV des bougies genere.',
        formatMessage: (payload) => buildSnapshotMessage(payload, 'Snapshot CSV des bougies genere.'),
      },
      'export-analyses': {
        path: 'pattern-analysis-export-import-csv',
        withQuery: false,
        fallbackMessage: 'Snapshot CSV des analyses genere.',
        formatMessage: (payload) => buildSnapshotMessage(payload, 'Snapshot CSV des analyses genere.'),
      },
    }[action];

    if (!actionConfig) {
      return;
    }

    setActionLoading(action);
    setActionMessage('');
    setError('');

    try {
      const requestUrl = actionConfig.withQuery
        ? `/api/admin/job/${actionConfig.path}?${buildQuery()}`
        : `/api/admin/job/${actionConfig.path}`;
      const response = await fetch(requestUrl, { method: 'POST' });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.status === 'FAILED') {
        throw new Error(payload?.message || 'Le job admin a echoue.');
      }
      setActionMessage(actionConfig.formatMessage
        ? actionConfig.formatMessage(payload)
        : (payload?.message || actionConfig.fallbackMessage));
      await loadStatus(false);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Erreur inconnue');
    } finally {
      setActionLoading('');
    }
  };

  const rows = useMemo(() => {
    const sourceRows = Array.isArray(statusData?.rows) ? statusData.rows : [];
    return sourceRows.filter((row) => {
      const matchesTimeframe = controls.timeframe === 'ALL' || row.timeframe === controls.timeframe;
      const matchesSymbol = !controls.symbolFilter
        || row.symbol?.toUpperCase().includes(controls.symbolFilter.trim().toUpperCase())
        || row.name?.toUpperCase().includes(controls.symbolFilter.trim().toUpperCase());
      return matchesTimeframe && matchesSymbol;
    });
  }, [controls.symbolFilter, controls.timeframe, statusData?.rows]);

  const timeframeStats = useMemo(() => {
    const timeframes = Array.isArray(statusData?.timeframes) ? statusData.timeframes : [];
    return timeframes.map((timeframe) => {
      const timeframeRows = (statusData?.rows || []).filter((row) => row.timeframe === timeframe);
      const readyRows = timeframeRows.filter((row) => row.bootstrapReady).length;
      const averageCoverage = timeframeRows.length
        ? Math.round(timeframeRows.reduce((total, row) => total + row.coveragePercent, 0) / timeframeRows.length)
        : 0;
      return {
        timeframe,
        readyRows,
        totalRows: timeframeRows.length,
        averageCoverage,
      };
    });
  }, [statusData?.rows, statusData?.timeframes]);

  const lastSummary = statusData?.lastSummary;

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Market Data" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Bootstrap historique Binance et couverture par actif
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Ce panneau permet de declencher le bootstrap profond ou la synchro incrementale, puis de suivre la couverture reelle des bougies par actif et par timeframe dans crypto_candle_history.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignSelf="flex-start">
              <MetricTile label="Etat" value={statusData?.running ? 'RUNNING' : 'IDLE'} tone={statusData?.running ? '#f3c65c' : '#8fd6b1'} />
              <MetricTile label="Exchange" value={controls.exchange} tone="#f7f3ea" />
              <MetricTile label="Univers" value={`Top ${Math.max(1, Number(controls.universeSize) || 100)}`} tone="#f3c65c" />
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Paper sx={{ flex: 0.9, p: 3, borderRadius: 5, background: '#fffaf2' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Commandes admin
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Le bootstrap remplit l'historique profond pour le top univers en 4H et 1D. La synchro incrementale maintient ensuite les series a jour sans les tronquer.
            </Typography>

            <Stack spacing={2.2}>
              <TextField
                label="Taille d'univers"
                type="number"
                value={controls.universeSize}
                onChange={(event) => setControls((current) => ({ ...current, universeSize: Number(event.target.value) || 100 }))}
                inputProps={{ min: 1, max: 300 }}
              />

              <FormControl fullWidth>
                <InputLabel id="pattern-market-data-exchange">Exchange</InputLabel>
                <Select
                  labelId="pattern-market-data-exchange"
                  value={controls.exchange}
                  label="Exchange"
                  onChange={(event) => setControls((current) => ({ ...current, exchange: event.target.value }))}
                >
                  <MenuItem value="BINANCE">BINANCE</MenuItem>
                </Select>
              </FormControl>

              <Stack spacing={1.4}>
                <Button
                  variant="contained"
                  onClick={() => handleAction('bootstrap')}
                  disabled={Boolean(actionLoading) || loading || Boolean(statusData?.running)}
                  startIcon={actionLoading === 'bootstrap' ? <CircularProgress size={18} color="inherit" /> : <RocketLaunchRoundedIcon />}
                  sx={{ minHeight: 52, borderRadius: 99, bgcolor: '#17323c', ':hover': { bgcolor: '#0f2329' } }}
                >
                  Bootstrap historique
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleAction('sync')}
                  disabled={Boolean(actionLoading) || loading || Boolean(statusData?.running)}
                  startIcon={actionLoading === 'sync' ? <CircularProgress size={18} color="inherit" /> : <CloudSyncRoundedIcon />}
                  sx={{ minHeight: 52, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
                >
                  Synchro incrementale
                </Button>
                <Button
                  variant="text"
                  onClick={() => loadStatus()}
                  disabled={loading}
                  startIcon={<RefreshRoundedIcon />}
                  sx={{ alignSelf: 'flex-start', color: '#17323c', fontWeight: 700 }}
                >
                  Rafraichir le statut
                </Button>
              </Stack>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f6f1e5', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Persistance dev via CSV d'import
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f', mb: 1.6 }}>
                  Ces snapshots reecrivent les fichiers charges par Liquibase au prochain demarrage pour conserver les bougies et l'historique des analyses hors de la base H2 memoire.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <Button
                    variant="outlined"
                    onClick={() => handleAction('export-candles')}
                    disabled={Boolean(actionLoading) || loading || Boolean(statusData?.running)}
                    startIcon={actionLoading === 'export-candles' ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                    sx={{ minHeight: 46, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
                  >
                    Snapshot bougies vers CSV
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleAction('export-analyses')}
                    disabled={Boolean(actionLoading) || loading || Boolean(statusData?.running)}
                    startIcon={actionLoading === 'export-analyses' ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                    sx={{ minHeight: 46, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
                  >
                    Snapshot analyses vers CSV
                  </Button>
                </Stack>
              </Paper>

              {error && <Alert severity="error">{error}</Alert>}
              {actionMessage && <Alert severity="success">{actionMessage}</Alert>}

              {statusData?.running && (
                <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f9f4ea', border: '1px solid rgba(18,44,52,0.08)' }}>
                  <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 1 }}>
                    Cycle en cours
                  </Typography>
                  <LinearProgress sx={{ borderRadius: 99, mb: 1.2 }} />
                  <Typography variant="body2" sx={{ color: '#4f646f' }}>
                    Le statut se rafraichit automatiquement tant que la synchro est active.
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ flex: 1.1, p: 3, borderRadius: 5, background: '#fff' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Derniere execution et couverture
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Ce resume montre le dernier cycle lance, puis la couverture moyenne par timeframe. Le detail complet se trouve dans le tableau ci-dessous.
            </Typography>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
              <StatCard label="Mode" value={lastSummary?.mode || 'n/a'} />
              <StatCard label="Statut" value={lastSummary?.status || 'n/a'} />
              <StatCard label="Series sync" value={lastSummary ? `${lastSummary.syncedSeries}/${lastSummary.requestedSeries}` : 'n/a'} />
              <StatCard label="Skipped" value={lastSummary ? `${lastSummary.skippedSeries}` : 'n/a'} />
              <StatCard label="Bougies stockees" value={lastSummary ? formatInteger(lastSummary.totalStoredCandles) : 'n/a'} />
            </Stack>

            {lastSummary?.message && (
              <Alert severity={lastSummary.status === 'SUCCESS' ? 'success' : lastSummary.status === 'SKIPPED' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {lastSummary.message}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <StatCard label="Debut" value={formatDateTime(lastSummary?.startedAt)} />
              <StatCard label="Fin" value={formatDateTime(lastSummary?.finishedAt)} />
              <StatCard label="Snapshot" value={formatDateTime(statusData?.generatedAt)} />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
              {timeframeStats.map((entry) => (
                <Paper key={entry.timeframe} elevation={0} sx={{ flex: 1, p: 2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <AutoGraphRoundedIcon sx={{ color: '#19323c', fontSize: 18 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c' }}>
                      {entry.timeframe}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#4f646f' }}>
                    Coverage moyenne: {entry.averageCoverage}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4f646f' }}>
                    Series prêtes: {entry.readyRows}/{entry.totalRows}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={entry.averageCoverage}
                    sx={{
                      mt: 1.2,
                      height: 8,
                      borderRadius: 99,
                      bgcolor: 'rgba(18,44,52,0.08)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 99,
                        bgcolor: entry.averageCoverage >= 100 ? '#157f3b' : '#19323c',
                      },
                    }}
                  />
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Stack>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
                Couverture historique par actif et timeframe
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                Chaque ligne correspond a une serie active dans l'univers cible. La couverture est comparee a l'objectif configure pour le bootstrap profond.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel id="pattern-market-data-timeframe">Timeframe</InputLabel>
                <Select
                  labelId="pattern-market-data-timeframe"
                  value={controls.timeframe}
                  label="Timeframe"
                  onChange={(event) => setControls((current) => ({ ...current, timeframe: event.target.value }))}
                >
                  <MenuItem value="ALL">Tous</MenuItem>
                  {(statusData?.timeframes || ['4H', '1D']).map((timeframe) => (
                    <MenuItem key={timeframe} value={timeframe}>{timeframe}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Filtrer par symbole"
                value={controls.symbolFilter}
                onChange={(event) => setControls((current) => ({ ...current, symbolFilter: event.target.value }))}
              />
            </Stack>
          </Stack>

          {loading ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                Chargement du statut des series historiques...
              </Typography>
            </Box>
          ) : rows.length === 0 ? (
            <Alert severity="info">Aucune serie ne correspond aux filtres courants.</Alert>
          ) : (
            <TableContainer sx={{ borderRadius: 4, border: '1px solid rgba(18,44,52,0.08)' }}>
              <Table size="small" sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f2ecde' }}>
                    <TableCell>Rang</TableCell>
                    <TableCell>Actif</TableCell>
                    <TableCell>Timeframe</TableCell>
                    <TableCell>Stocke / cible</TableCell>
                    <TableCell>Coverage</TableCell>
                    <TableCell>Etat</TableCell>
                    <TableCell>Premiere bougie</TableCell>
                    <TableCell>Derniere bougie</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={`${row.symbol}-${row.timeframe}`} hover>
                      <TableCell>{row.marketCapRank || 'n/a'}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700 }}>{row.symbol}</Typography>
                        <Typography variant="caption" sx={{ color: '#6c7a80' }}>{row.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.timeframe} size="small" sx={{ bgcolor: '#eef4f6', color: '#19323c', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>{formatInteger(row.storedCandles)} / {formatInteger(row.targetCandles)}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack spacing={0.7}>
                          <LinearProgress
                            variant="determinate"
                            value={row.coveragePercent}
                            sx={{
                              height: 8,
                              borderRadius: 99,
                              bgcolor: 'rgba(18,44,52,0.08)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 99,
                                bgcolor: row.bootstrapReady ? '#157f3b' : '#19323c',
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#4f646f' }}>{row.coveragePercent}%</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.bootstrapReady ? 'Ready' : 'Partial'}
                          size="small"
                          sx={{
                            bgcolor: row.bootstrapReady ? '#d7f3df' : '#f5ebc8',
                            color: '#19323c',
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>{formatDateTime(row.firstCandleTimestamp)}</TableCell>
                      <TableCell>{formatDateTime(row.lastCandleTimestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

const MetricTile = ({ label, value, tone }) => (
  <Paper
    elevation={0}
    sx={{
      minWidth: 110,
      p: 2,
      borderRadius: 4,
      bgcolor: 'rgba(247,243,234,0.1)',
      color: '#f7f3ea',
      border: `1px solid ${tone}33`,
    }}
  >
    <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.7)' }}>{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 800, color: tone }}>{value}</Typography>
  </Paper>
);

const StatCard = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      px: 2,
      py: 1.6,
      borderRadius: 3,
      border: '1px solid rgba(18,44,52,0.08)',
      bgcolor: '#fffdf8',
      minWidth: 140,
    }}
  >
    <Typography variant="caption" sx={{ color: '#6c7a80' }}>{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 800, color: '#19323c' }}>{value}</Typography>
  </Paper>
);

export default PatternMarketDataAdminPage;